import React, { useState, useEffect, useCallback } from "react";
import { IndosbookMark } from "./IndosbookLogo";
import CookieConsent from "./CookieConsent";
import { Download, X, Smartphone, Share, MonitorSmartphone } from "lucide-react";

const LS_INSTALL_DISMISSED = "indosbook_install_dismissed";

// Custom PWA install prompt. Listens for the browser's beforeinstallprompt
// event and shows a branded install card with an option to dismiss.
const InstallAppPrompt = ({ show }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installed, setInstalled] = useState(false);

  // capture the install event early
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // detect iOS (no beforeinstallprompt) + already installed
    const ios =
      /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setInstalled(standalone);

    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // show only when the parent says so (after cookies) & not dismissed/installed
  useEffect(() => {
    if (!show || installed) return;
    if (localStorage.getItem(LS_INSTALL_DISMISSED)) return;
    if (deferredPrompt || isIOS) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [show, deferredPrompt, isIOS, installed]);

  const dismiss = () => {
    localStorage.setItem(LS_INSTALL_DISMISSED, "1");
    setVisible(false);
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setVisible(false);
        localStorage.setItem(LS_INSTALL_DISMISSED, "1");
      }
      setDeferredPrompt(null);
    }
  };

  if (!visible || installed) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] px-4 pb-4 sm:bottom-6 sm:pb-0">
      <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-50">
            <IndosbookMark size={34} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold text-gray-900">Install Indosbook</p>
              <button
                onClick={dismiss}
                aria-label="Not now"
                className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-1 text-sm leading-relaxed text-gray-600">
              Add Indosbook to your home screen for a faster, app-like
              experience.
            </p>

            {isIOS ? (
              // iOS doesn't support beforeinstallprompt — show manual steps
              <div className="mt-3 rounded-xl bg-gray-50 p-3 text-xs leading-relaxed text-gray-600">
                <span className="flex items-center gap-1.5 font-semibold text-gray-800">
                  <Smartphone className="h-4 w-4 text-[#FF4500]" /> On iPhone / iPad
                </span>
                <ol className="mt-1.5 list-decimal space-y-1 pl-4">
                  <li>
                    Tap the <Share className="inline h-3.5 w-3.5 text-[#002855]" />{" "}
                    Share button
                  </li>
                  <li>Scroll and tap "Add to Home Screen"</li>
                </ol>
                <button
                  onClick={dismiss}
                  className="mt-2.5 h-9 w-full rounded-full bg-[#FF4500] text-sm font-bold text-white"
                >
                  Got it
                </button>
              </div>
            ) : (
              <button
                onClick={install}
                className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#FF4500] text-sm font-bold text-white transition-colors hover:bg-[#e03d00]"
              >
                <MonitorSmartphone className="h-4 w-4" /> Install app
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper that orchestrates: cookies first, then install prompt.
export const FirstVisitOverlays = () => {
  const [cookiesDone, setCookiesDone] = useState(false);
  const handleAccepted = useCallback(() => setCookiesDone(true), []);
  return (
    <>
      <CookieConsent onAccepted={handleAccepted} />
      <InstallAppPrompt show={cookiesDone} />
    </>
  );
};

export default InstallAppPrompt;
